import React, { ReactNode, useEffect, useState } from 'react';
import Button from '../../common/Button';
import { MemberDTO } from '../../../services/apiClient';
import FormField from '../../common/FormField';
import handleChange from '../../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../../hooks/useFormValidation';
import { useUserApi } from '../../../hooks/useUserApi';
import { faL } from '@fortawesome/free-solid-svg-icons';

interface Props {
  member: MemberDTO;
  onChange: (updated: Partial<MemberDTO>) => void;
  onNext: () => void;
  onBack: () => void;
}

const ContactInfo: React.FC<Props> = ({ member, onChange, onNext, onBack }) => {
  const [formData, setFormData] = useState<MemberDTO>(member);
  const { checkEmailExists, checkPhoneNumberExists, isLoading } = useUserApi();
  const [emailExistsError, setEmailExistsError] = useState<string>('');
  const [phoneExistsError, setPhoneExistsError] = useState<string>('');

  const validationRules: ValidationRules = {
    email: [
      { required: true, message: 'Email is required' },
      {
        custom: (value) => {
          if (!value) return true;
          return validateEmail(String(value));
        },
        message: 'Email is not valid',
      },
    ],
    phone: [
      { required: true, message: 'Cell Phone is required' },
      {
        custom: (value) => {
          if (!value) return true;
          return validateSACellNumber(String(value));
        },
        message: 'Cell Phone is not valid',
      },
    ],
  };
  const { errors, validate } = useFormValidation<MemberDTO>(validationRules);

  //CALL USE API HOOK => HERE CHECK IF EXISTS
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // if empty, let "required" handle it
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email.trim());
  };

  //CALL USE API HOOK => HERE CHECK IF EXISTS
  const validateSACellNumber = (cellNumber: string): boolean => {
    if (!cellNumber) return true; // let "required" handle empty
    const cleaned = cellNumber.replace(/\s+/g, ''); // remove spaces
    const pattern = /^(0(6|7|8)[0-9]{8}|27(6|7|8)[0-9]{8})$/;
    return pattern.test(cleaned);
  };

  const handleEmailExists = async (email: string | undefined) => {
    if (email) {
      try {
        const response = await checkEmailExists(email);
        if (response?.data === true) {
          setEmailExistsError('This email address is already registered');
          return true;
        } else {
          setEmailExistsError('');
          return false;
        }
      } catch (error) {
        console.error('Error checking email existence:', error);
        return false;
      }
    }
  };

  const handlePhoneNumberExists = async (phone: string | undefined) => {
    if (phone) {
      try {
        const response = await checkPhoneNumberExists(phone);
        if (response?.data === true) {
          setPhoneExistsError('This phone number is already registered');
          return true;
        } else {
          setPhoneExistsError('');
          return false;
        }
      } catch (error) {
        console.error('Error checking phone existence:', error);
        return false;
      }
    }
  };

  const handleNext = async () => {
    const emailExists = await handleEmailExists(formData.email);
    const phoneExists = await handlePhoneNumberExists(formData.phone);
    if (validate(formData) && !emailExists && !phoneExists) {
      onNext();
    }
  };

  return (
    <div>
      <FormField
        id="email"
        label="Email (Used for OTP and Communication)"
        type="email"
        value={formData.email || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        textCase='lowercase'
        placeholder="e.g john@doe.com"
        required={true}
        error={errors.email || emailExistsError}
      />
      <FormField
        id="phone"
        label="Cell Phone (Used for OTP and Communication)"
        type="text"
        value={formData.phone || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="e.g 0821234567"
        required={true}
        error={errors.phone || phoneExistsError}
      />
      <FormField
        id="landlinePhone"
        label="Alternative Contact"
        type="text"
        value={formData.landlinePhone || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="e.g 0111234567"
        required={false}
        error={errors.landlinePhone}
      />

      <div className="mt-4 flex justify-between w-full">
        <Button onClick={onBack} variant="secondary">
          Back
        </Button>
        <Button onClick={handleNext} variant="primary">
          Next
        </Button>
      </div>
    </div>
  );
};

export default ContactInfo;
